import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getCompetitionCompetiteursResults,
  getCompetitionCompetiteurs,
} from "../../services/competiteursServices";
import { getCompetitionById } from "../../services/competitionsServices";
import {
  getOngoingDistributions,
  getFinishedDistributions,
} from "../../services/distributionsServices";

import { getAllPartenaires } from "../../services/partenairesServices"; // ✅ AJOUT

import ScrollIndicator from "../../hooks/ScrollIndicator";
import useScrollToTop from "../../hooks/useScrollToTop";

import PublicStatisticsCard from "./PublicStatisticsCard";
import PublicPhotoGallery from "./PublicPhotoGallery";
import PublicFiltersBar from "./components/PublicFiltersBar";
import PublicResultsTable from "./components/PublicResultsTable";
import PublicOngoingTable from "./components/PublicOngoingTable";
import PublicPodiums from "./components/PublicPodiums";
import PublicCompetitorSheet from "./components/PublicCompetitorSheet";

import CompetitionPartnersSection from "./components/CompetitionPartnersSection"; // ✅ AJOUT
import PublicPromoPanel from "./components/PublicPromoPanel";
import { getCompetitionCombats } from "../../services/combatsServices";

// ✅ Helper : extraire user_id organisateur depuis la compétition
const extractOrganisateurUserId = (competition) => {
  if (!competition) return null;

  // meilleur cas : user_id déjà direct dans la compétition
  const direct = competition.user_id ?? competition.organisateur_user_id;
  if (direct !== undefined && direct !== null && String(direct).trim() !== "") {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const v = competition.organisateur_competition;

  if (typeof v === "number") return v;

  if (typeof v === "string") {
    const s = v.trim();
    if (!s) return null;
    if (/^\d+$/.test(s)) return Number(s);

    // JSON stringifié possible
    try {
      const parsed = JSON.parse(s);
      const candidate = parsed?.user_id ?? parsed?.userId ?? parsed?.id;
      const n = Number(candidate);
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }

  if (typeof v === "object" && v) {
    const candidate = v.user_id ?? v.userId ?? v.id;
    const n = Number(candidate);
    return Number.isFinite(n) ? n : null;
  }

  return null;
};

const CompetitionResultsPublic = () => {
  useScrollToTop();
  const { competition_id } = useParams();

  const [loading, setLoading] = useState(true);
  const [competition, setCompetition] = useState(null);

  const [results, setResults] = useState([]); // résultats (par judoka)
  const [allCompetiteurs, setAllCompetiteurs] = useState([]); // liste brute
  const [ongoingDistributions, setOngoingDistributions] = useState([]);
  const [finishedDistributions, setFinishedDistributions] = useState([]);

  // ✅ Partenaires
  const [loadingPartenaires, setLoadingPartenaires] = useState(false);
  const [partenaires, setPartenaires] = useState([]);

  // UI
  const [tab, setTab] = useState("results"); // "results" | "ongoing" | "podiums"
  const [search, setSearch] = useState(""); // club OU nom/prénom
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedPoids, setSelectedPoids] = useState("");
  const [selectedGender, setSelectedGender] = useState(""); // "M" | "F" | ""

  // Fiche judoka
  const [sheetCompetitor, setSheetCompetitor] = useState(null);

  const [competitionCombats, setCompetitionCombats] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadingPartenaires(true);

      try {
        const [
          compData,
          resData,
          ongoingDists,
          finishedDists,
          allComps,
          allParts,
          combatsData,
        ] = await Promise.all([
          getCompetitionById(competition_id),
          getCompetitionCompetiteursResults(competition_id),
          getOngoingDistributions(competition_id),
          getFinishedDistributions(competition_id),
          getCompetitionCompetiteurs(competition_id),
          getAllPartenaires(), // ✅ on récupère tout puis on filtre côté front
          getCompetitionCombats(competition_id),
        ]);

        setCompetition(compData || null);
        setResults(resData || []);
        setOngoingDistributions(ongoingDists || []);
        setFinishedDistributions(finishedDists || []);
        setAllCompetiteurs(allComps || []);

        // ✅ Filtrer les partenaires de l’organisateur
        const organisateurUserId = extractOrganisateurUserId(compData);

        const list = (Array.isArray(allParts) ? allParts : [])
          .filter((p) => Number(p.user_id) === Number(organisateurUserId))
          .filter((p) => p.is_active === true || p.is_active === 1)
          .sort((a, b) => (a.ordre ?? 9999) - (b.ordre ?? 9999));

        setPartenaires(list);
      } catch (e) {
        console.error("Erreur chargement page publique:", e);
        setPartenaires([]);
      } finally {
        setLoading(false);
        setLoadingPartenaires(false);
      }
    })();
  }, [competition_id]);

  // ===== Index distribution (fusion en cours + terminées) =====
  const distIndex = useMemo(() => {
    const idx = new Map();

    const parseIds = (raw) => {
      if (Array.isArray(raw)) return raw.map(Number).filter(Number.isFinite);
      try {
        return JSON.parse(raw || "[]")
          .map(Number)
          .filter(Number.isFinite);
      } catch {
        return [];
      }
    };

    const isNCFormula = (f) => /non_classe/i.test(String(f || ""));
    const isPoule = (f) => /^poule_/i.test(String(f || ""));
    const isTableau = (f) => /tableau/i.test(String(f || ""));

    const scoreDist = (x) => {
      // plus haut = plus prioritaire
      let s = 0;
      if (x.isFinished) s += 100;
      if (isTableau(x.formule)) s += 50;
      if (isPoule(x.formule)) s -= 5;
      if (isNCFormula(x.formule)) s -= 1000;
      return s;
    };

    const addDist = (d, isFinished) => {
      const ids = parseIds(d?.competiteurs);
      ids.forEach((cid) => {
        const entry = {
          distId: d.id,
          tapis: Number(d.numero_tapis) || null,
          categorie_age: d.categorie_age,
          categorie_poids: d.categorie_poids,
          formule: d.formule_repartition,
          isFinished: !!isFinished,
        };
        const arr = idx.get(cid) || [];
        arr.push(entry);
        idx.set(cid, arr);
      });
    };

    (ongoingDistributions || []).forEach((d) => addDist(d, false));
    (finishedDistributions || []).forEach((d) => addDist(d, true));

    // trie par priorité (tableau terminé en premier)
    for (const [cid, arr] of idx.entries()) {
      const sorted = [...arr].sort((a, b) => {
        const sa = scoreDist(a);
        const sb = scoreDist(b);
        if (sa !== sb) return sb - sa;
        // en cas d'égalité, on prend la dist la plus "récente" (id plus grand)
        return Number(b.distId) - Number(a.distId);
      });
      idx.set(cid, sorted);
    }

    return idx;
  }, [ongoingDistributions, finishedDistributions]);

  // Map par id de distribution (pour retrouver le tapis depuis un combat)
  const distById = useMemo(() => {
    const all = [
      ...(ongoingDistributions || []),
      ...(finishedDistributions || []),
    ];
    return new Map(all.map((d) => [Number(d.id), d]));
  }, [ongoingDistributions, finishedDistributions]);

  const compIndex = useMemo(
    () => new Map((allCompetiteurs || []).map((c) => [Number(c.id), c])),
    [allCompetiteurs],
  );

  // ===== Clubs (depuis résultats + en-cours) =====
  const clubs = useMemo(() => {
    return [
      ...new Set(
        (allCompetiteurs || []).map((c) => c.club_competiteur).filter(Boolean),
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [allCompetiteurs]);

  // ✅ Map des résultats classés par id (backend "results")
  const resultsById = useMemo(() => {
    const m = new Map();

    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };

    const rankNumOrNull = (v) => {
      const n = Number(v);
      return Number.isFinite(n) && n > 0 ? n : null;
    };

    (results || []).forEach((r) => {
      const id = Number(r?.id);
      if (!Number.isFinite(id)) return;

      const prev = m.get(id);

      const addPoints = toNum(r?.total_points);
      const addWins = toNum(r?.total_victoires);
      const rank = rankNumOrNull(r?.classement_position);

      if (!prev) {
        m.set(id, {
          ...r,
          id,
          total_points: addPoints,
          total_victoires: addWins,
          _bestRank: rank, // interne
          classement_position: rank ?? r?.classement_position,
        });
        return;
      }

      const prevBest = prev._bestRank ?? null;
      const nextBest =
        rank != null
          ? prevBest == null
            ? rank
            : Math.min(prevBest, rank)
          : prevBest;

      m.set(id, {
        ...prev,
        // on garde les champs texte si déjà présents, sinon on complète
        nom_competiteur: prev.nom_competiteur ?? r.nom_competiteur,
        prenom_competiteur: prev.prenom_competiteur ?? r.prenom_competiteur,
        club_competiteur: prev.club_competiteur ?? r.club_competiteur,

        total_points: toNum(prev.total_points) + addPoints,
        total_victoires: toNum(prev.total_victoires) + addWins,

        _bestRank: nextBest,
        classement_position: nextBest ?? prev.classement_position,
      });
    });

    return m;
  }, [results]);

  const categoryOptions = useMemo(() => {
    const all = [
      ...(ongoingDistributions || []),
      ...(finishedDistributions || []),
    ];

    const ages = Array.from(
      new Set(all.map((d) => d?.categorie_age).filter(Boolean)),
    ).sort((a, b) => String(a).localeCompare(String(b)));

    const poids = Array.from(
      new Set(all.map((d) => d?.categorie_poids).filter(Boolean)),
    ).sort((a, b) => String(a).localeCompare(String(b)));

    return { ages, poids };
  }, [ongoingDistributions, finishedDistributions]);

  const computedById = useMemo(() => {
    const m = new Map();

    const addOne = (id, myPts, oppPts, resLetter) => {
      const cid = Number(id);
      if (!Number.isFinite(cid)) return;

      const prev = m.get(cid) || { fights: 0, wins: 0, points: 0 };
      prev.fights += 1;

      const my = Number(myPts);
      if (Number.isFinite(my)) prev.points += my;

      const L = String(resLetter || "")
        .trim()
        .toUpperCase();
      if (L === "V") prev.wins += 1;
      else if (!L) {
        const op = Number(oppPts);
        if (Number.isFinite(my) && Number.isFinite(op) && my > op)
          prev.wins += 1;
      }

      m.set(cid, prev);
    };

    (competitionCombats || []).forEach((f) => {
      addOne(
        f.competiteur_id1,
        f.points_competiteur1,
        f.points_competiteur2,
        f.resultat_competiteur1,
      );
      addOne(
        f.competiteur_id2,
        f.points_competiteur2,
        f.points_competiteur1,
        f.resultat_competiteur2,
      );
    });

    return m;
  }, [competitionCombats]);

  // ===== Enrichissement des résultats par judoka (✅ inclut NC) =====
  const enrichedResults = useMemo(() => {
    // base = tous les compétiteurs présents dans une distribution terminée
    // + ceux déjà présents dans results (sécurité)
    const base = (allCompetiteurs || []).filter((c) => {
      const id = Number(c.id);
      const diList = distIndex.get(id) || [];
      const hasFinished = diList.some((x) => x?.isFinished === true);
      return hasFinished || resultsById.has(id);
    });

    return base.map((c) => {
      const id = Number(c.id);
      const r = resultsById.get(id); // désormais agrégé poule+tableau
      const diList = distIndex.get(id) || [];
      const di = diList[0]; // tableau terminé prioritaire

      const merged = { ...c, ...(r || {}) };
      // ✅ On force les totaux depuis les combats (poule + tableau)
      const agg = computedById.get(id);
      if (agg) {
        merged.total_points = agg.points;
        merged.total_victoires = agg.wins;
      }

      // infos distribution
      merged._distId = di?.distId ?? null;
      merged._tapis = di?.tapis ?? null;
      merged._age = di?.categorie_age ?? null;
      merged._poids = di?.categorie_poids ?? null;
      merged._formule = di?.formule ?? null;
      merged._isFinished = di?.isFinished ?? null;

      // ✅ si pas de classement => NC
      const rankNum = Number(merged.classement_position);
      const hasRank = Number.isFinite(rankNum) && rankNum > 0;
      if (!hasRank) merged.classement_position = "NC";

      // defaults (évite undefined)
      if (merged.total_points == null) merged.total_points = 0;
      if (merged.total_victoires == null) merged.total_victoires = 0;

      return merged;
    });
  }, [allCompetiteurs, distIndex, resultsById, computedById]);

  // ===== Liste "en cours" (judokas présents dans distributed en cours) =====
  const ongoingCompetiteurs = useMemo(() => {
    const out = [];
    (ongoingDistributions || []).forEach((d) => {
      const ids = Array.isArray(d.competiteurs)
        ? d.competiteurs.map(Number)
        : JSON.parse(d.competiteurs || "[]").map(Number);
      const details = (allCompetiteurs || []).filter((c) => ids.includes(c.id));
      details.forEach((comp) =>
        out.push({
          ...comp,
          tapis: d.numero_tapis,
          categorie_age: d.categorie_age,
          categorie_poids: d.categorie_poids,
          distribution_id: d.id,
        }),
      );
    });
    return out;
  }, [ongoingDistributions, allCompetiteurs]);

  // ===== Filtres globaux (recherche club/nom + select club) =====
  const filteredResults = useMemo(() => {
    const q = search.trim().toLowerCase();

    return enrichedResults.filter((c) => {
      const matchClub = !selectedClub || c.club_competiteur === selectedClub;

      const matchQ =
        !q ||
        [c.nom_competiteur, c.prenom_competiteur, c.club_competiteur]
          .join(" ")
          .toLowerCase()
          .includes(q);

      const matchAge =
        !selectedAge || String(c._age || "") === String(selectedAge);
      const matchPoids =
        !selectedPoids || String(c._poids || "") === String(selectedPoids);

      const g = String(c.genre_competiteur || "").toUpperCase();
      const matchGender =
        !selectedGender || g === String(selectedGender).toUpperCase();

      return matchClub && matchQ && matchAge && matchPoids && matchGender;
    });
  }, [
    enrichedResults,
    search,
    selectedClub,
    selectedAge,
    selectedPoids,
    selectedGender,
  ]);

  const filteredOngoing = useMemo(() => {
    const q = search.trim().toLowerCase();

    return ongoingCompetiteurs.filter((c) => {
      const matchClub = !selectedClub || c.club_competiteur === selectedClub;

      const matchQ =
        !q ||
        [c.nom_competiteur, c.prenom_competiteur, c.club_competiteur]
          .join(" ")
          .toLowerCase()
          .includes(q);

      const matchAge =
        !selectedAge || String(c.categorie_age || "") === String(selectedAge);

      const matchPoids =
        !selectedPoids ||
        String(c.categorie_poids || "") === String(selectedPoids);

      const g = String(c.genre_competiteur || "").toUpperCase();
      const matchGender =
        !selectedGender || g === String(selectedGender).toUpperCase();

      return matchClub && matchQ && matchAge && matchPoids && matchGender;
    });
  }, [
    ongoingCompetiteurs,
    search,
    selectedClub,
    selectedAge,
    selectedPoids,
    selectedGender,
  ]);

  // ===== Podiums (classements par distribution terminée) =====
  // ===== Podiums (✅ inclut NC + distributions vides) =====
  const podiums = useMemo(() => {
    const parseIds = (raw) => {
      if (Array.isArray(raw))
        return raw.map(Number).filter((n) => Number.isFinite(n));
      try {
        return JSON.parse(raw || "[]")
          .map(Number)
          .filter((n) => Number.isFinite(n));
      } catch {
        return [];
      }
    };

    const isNCFormula = (f) =>
      String(f || "")
        .toLowerCase()
        .includes("non_classe");

    const rankKey = (entry) => {
      const n = Number(entry?.classement_position);
      if (Number.isFinite(n) && n > 0) return { g: 0, v: n };
      if (String(entry?._rankLabel || "").toUpperCase() === "NC")
        return { g: 1, v: 9999 };
      return { g: 2, v: 9999 }; // "—" en bas
    };

    return (finishedDistributions || []).map((d) => {
      const ids = parseIds(d.competiteurs);
      const nc = isNCFormula(d.formule_repartition);

      const entries = ids
        .map((cid) => {
          const base = compIndex.get(Number(cid)); // depuis allCompetiteurs
          const r = resultsById.get(Number(cid)); // depuis résultats (classés)
          if (!base && !r) return null;

          const merged = { ...(base || {}), ...(r || {}), id: Number(cid) };

          const rankNum = Number(merged.classement_position);
          const hasRank = Number.isFinite(rankNum) && rankNum > 0;

          // ✅ label affiché : 1/2/3.. sinon NC (si formule NC) sinon —
          merged._rankLabel = hasRank ? String(rankNum) : nc ? "NC" : "—";

          // defaults (sécurité)
          if (merged.total_points == null) merged.total_points = 0;
          if (merged.total_victoires == null) merged.total_victoires = 0;

          return merged;
        })
        .filter(Boolean)
        .sort((a, b) => {
          const A = rankKey(a);
          const B = rankKey(b);
          if (A.g !== B.g) return A.g - B.g;
          if (A.v !== B.v) return A.v - B.v;
          return String(a.nom_competiteur || "").localeCompare(
            String(b.nom_competiteur || ""),
          );
        });

      // ✅ on retourne aussi les distributions vides (entries peut être [])
      return { distribution: d, entries };
    });
  }, [finishedDistributions, compIndex, resultsById]);

  const filteredPodiums = useMemo(() => {
    const q = search.trim().toLowerCase();

    return (
      (podiums || [])
        // filtre par catégorie (via distribution)
        .filter(({ distribution: d }) => {
          if (!d) return false;

          const matchAge =
            !selectedAge ||
            String(d.categorie_age || "") === String(selectedAge);
          const matchPoids =
            !selectedPoids ||
            String(d.categorie_poids || "") === String(selectedPoids);

          const dg = String(d.genre || "").toUpperCase();
          const matchGender =
            !selectedGender || dg === String(selectedGender).toUpperCase();

          return matchAge && matchPoids && matchGender;
        })
        // filtre search + club au niveau des entries
        .map(({ distribution, entries }) => {
          let e = Array.isArray(entries) ? entries : [];

          if (selectedClub)
            e = e.filter((c) => c.club_competiteur === selectedClub);

          if (q) {
            e = e.filter((c) =>
              [c.nom_competiteur, c.prenom_competiteur, c.club_competiteur]
                .join(" ")
                .toLowerCase()
                .includes(q),
            );
          }

          return { distribution, entries: e };
        })
        // on retire les distributions sans entries après filtre
        .filter(({ entries }) => entries.length > 0)
    );
  }, [
    podiums,
    search,
    selectedClub,
    selectedAge,
    selectedPoids,
    selectedGender,
  ]);

  // ===== Stat Clubs =====
  const nombreClubs = useMemo(() => {
    const set = new Set((allCompetiteurs || []).map((c) => c.club_competiteur));
    return set.size;
  }, [allCompetiteurs]);

  if (loading) {
    return <p className="text-center text-gray-500 mt-[150px]">Chargement…</p>;
  }

  return (
    <div className="flex flex-col w-full mx-auto p-2 mt-[130px] dark:text-white">
      <ScrollIndicator />
      <div className="text-center space-y-2 mb-6">
        <h2 className="text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
          Visiteurs
        </h2>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          Résultats — {competition?.nom_competition}
        </h1>
      </div>

      <PublicPromoPanel
      // photoUrl="/img/laurent.jpg" // optionnel, tu activeras quand tu auras l’image
      />

      {/* Stats + Photos */}
      <PublicStatisticsCard nombreClubs={nombreClubs} />
      <PublicPhotoGallery competitionId={competition_id} />

      {/* Filtres globaux */}
      <PublicFiltersBar
        search={search}
        setSearch={setSearch}
        selectedClub={selectedClub}
        setSelectedClub={setSelectedClub}
        clubs={clubs}
        tab={tab}
        setTab={setTab}
        selectedAge={selectedAge}
        setSelectedAge={setSelectedAge}
        selectedPoids={selectedPoids}
        setSelectedPoids={setSelectedPoids}
        selectedGender={selectedGender}
        setSelectedGender={setSelectedGender}
        ageOptions={categoryOptions.ages}
        poidsOptions={categoryOptions.poids}
      />

      {/* Onglets */}
      <div className="max-w-[1280px] w-full mx-auto mt-4 space-y-4">
        {tab === "results" && (
          <PublicResultsTable
            rows={filteredResults}
            distById={distById}
            onOpenSheet={setSheetCompetitor}
          />
        )}

        {tab === "ongoing" && (
          <PublicOngoingTable
            rows={filteredOngoing}
            distById={distById} // <<< AJOUT
            onOpenSheet={(comp) =>
              setSheetCompetitor({
                ...comp,
                _distId: comp.distribution_id ?? null,
                _tapis: comp.tapis ?? null,
                _age: comp.categorie_age ?? null,
                _poids: comp.categorie_poids ?? null,
              })
            }
          />
        )}

        {tab === "podiums" && <PublicPodiums data={filteredPodiums} />}

        {/* ✅ PARTENAIRES (à la suite des résultats/ongoing/podiums) */}
        <CompetitionPartnersSection
          partenaires={partenaires}
          loading={loadingPartenaires}
        />
      </div>

      {/* Fiche judoka (drawer/modal) */}
      <PublicCompetitorSheet
        competitor={sheetCompetitor}
        onClose={() => setSheetCompetitor(null)}
        compIndex={compIndex}
        distById={distById}
      />
    </div>
  );
};

export default CompetitionResultsPublic;
